import "./AccountSettings.scss"

import {
  InlineNotification,
  InlineNotificationVariant,
  PromptVariant,
} from "@skylead/component-library"
import React, { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import settings from "../../../constants/settings"
import * as actions from "../../../redux/actions"
import { twoFactorConfigurationModal } from "../../../utils/2fa-utils"
import { useConfig, useGlobalTranslation } from "../../../utils/hook"
import { hasWhitelabelPermission, WHITELABEL_PERMISSIONS } from "../../../utils/whitelabel-utils"
import Button, { ButtonVariant } from "../../atoms/Button"
import ChangePassword from "../../atoms/ChangePassword"
import LangText from "../../atoms/LangText"
import InputGroup from "../../organisms/InputGroup"

const AccountSettings = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { tt } = useGlobalTranslation()

  const { apiKey, canActivateWarmup, isWarmupActive, userData, userTeams } = useSelector(state => ({
    apiKey: state.user.openApiKey,
    canActivateWarmup: state.user.profile.canActivateWarmup,
    isWarmupActive: state.user.profile.isWarmupActive,
    userData: state.user.profile,
    userTeams: state.teamManagement.userTeams.items,
  }))

  const config = useConfig()

  const laodUserData = async () => {
    await dispatch(actions.getUserData(undefined, navigate))
  }

  useEffect(() => {
    laodUserData()
  }, [])

  const handleDeleteUser = async () => {
    await dispatch(actions.deleteUser(tt, navigate))
    dispatch(actions.closeModal())
  }

  const handleActivateWarmup = async () => {
    await dispatch(actions.activateWarmUp())
    laodUserData()
  }

  const isTeamOwner = userTeams && userTeams.find(team => team.isTeamOwner)

  const is2FaActive = userData?.is2FAActive

  const canDeleteAccount = () => {
    if (
      (userTeams.length === 1 &&
        isTeamOwner &&
        (userTeams[0].accounts === null || userTeams[0].accounts?.length === 0)) ||
      userTeams.length === 0
    ) {
      return true
    }

    if (userTeams.length > 0) {
      return false
    }
  }

  const deleteAccountConfirmation = () => {
    if (canDeleteAccount()) {
      dispatch(
        actions.showPrompt(
          PromptVariant.WARNING,
          tt("warning"),
          tt("account.settings.can-delete-account.prompt.msg"),
          handleDeleteUser,
          tt("delete-account"),
        ),
      )
    } else {
      dispatch(
        actions.showPrompt(
          PromptVariant.ERROR,
          tt("warning"),
          tt("account.settings.can-not-delete-account.prompt.msg"),
        ),
      )
    }
  }

  return (
    <div className="account-settings-wrapper">
      <div className="account-settings-wrapper-setting">
        <div className="name-and-description">
          <span className="large">
            <LangText langKey="password-change" />
          </span>
          <span className="small">
            <LangText langKey="account.settings.password-change.msg" />
          </span>
        </div>

        <div className="account-setting-container">
          <ChangePassword />
        </div>
      </div>

      <div className="account-settings-wrapper-setting">
        <div className="name-and-description">
          <span className="large">
            <LangText langKey="two-factor-authentication" />
          </span>
          <span className="small">
            <LangText langKey="account.settings.to-fa.msg" />
          </span>
        </div>

        {!is2FaActive ? (
          <div className="account-setting-control">
            <Button
              variant={ButtonVariant.SECONDARY}
              icon="shield-lock-20"
              onClick={async () => {
                await dispatch(actions.generateTwoFactorSecretUri())
                twoFactorConfigurationModal(tt)
              }}
            >
              <LangText langKey="account.settings.set-to-fa" />
            </Button>
          </div>
        ) : (
          <div className="account-setting-control">
            <InlineNotification
              type={InlineNotificationVariant.SUCCESS}
              label={tt("account.settings.set-to-fa.notification.label")}
            />
            <Button
              variant={ButtonVariant.DANGER_SECONDARY}
              icon="shield-lock-20"
              onClick={() => dispatch(actions.deactivateTwoFactor(tt, navigate))}
            >
              <LangText langKey="disable-to-fa" />
            </Button>
          </div>
        )}
      </div>

      {settings.featuresActive.includes("email-warmup") &&
        hasWhitelabelPermission(WHITELABEL_PERMISSIONS.EMAIL_WARMUP) && (
          <div className="account-settings-wrapper-setting">
            <div className="name-and-description">
              <span className="large">
                <LangText langKey="account.settings.email-warm-up.label" />
              </span>
              <span className="small">
                <LangText langKey="account.settings.email-warm-up.msg" />
              </span>
            </div>

            <div className="account-setting-control">
              {isWarmupActive ? (
                <InlineNotification
                  type={InlineNotificationVariant.SUCCESS}
                  label={tt("account.settings.active-warm-up.label")}
                />
              ) : (
                <Button
                  variant={ButtonVariant.SECONDARY}
                  icon="mail-fire-20"
                  disabled={!canActivateWarmup}
                  onClick={handleActivateWarmup}
                >
                  <LangText langKey="enable" />
                </Button>
              )}
            </div>
          </div>
        )}

      <div className="account-settings-wrapper-setting">
        <div className="name-and-description">
          <span className="large">
            <LangText langKey="account.settings.api.msg.1" />
          </span>
          <span className="small">
            <LangText langKey="account.settings.api.msg.2" /> {config.REACT_APP_COMPANY_NAME}{" "}
            <a
              href="https://documenter.getpostman.com/view/7428744/UV5ZAGMg"
              className="api-link"
              rel="noopener noreferrer"
              target="_blank"
              style={{ color: "#0080ff" }}
            >
              <LangText langKey="api-document" />
            </a>
            .
          </span>
        </div>

        <div className="account-setting-control">
          <InputGroup
            inputGroupClassName="input-on-surface"
            label={tt("api-key")}
            customValue={apiKey}
            canCopyText
          />
        </div>
      </div>

      <div className="account-settings-wrapper-setting">
        <div className="name-and-description">
          <span className="large">
            <LangText langKey="account.settings.account-deletion.label" />
          </span>
          <span className="small">
            <LangText langKey="account.settings.account-deletion.msg" />
          </span>
        </div>

        <div className="account-setting-control">
          <Button
            variant={ButtonVariant.DANGER}
            icon="trash-20"
            onClick={deleteAccountConfirmation}
          >
            <LangText langKey="delete-account" />
          </Button>
        </div>
      </div>
    </div>
  )
}

export default AccountSettings
